import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import type {
  SkillDataTableItem,
  SkillListStateFilter,
} from '@kirin/api-client/contracts/skills';
import type { OrganizationId } from '@kirin/api-client/contracts/organizations';
import { SUPPORT_READONLY_TOOLTIP } from '@/components/alertActionTooltipCopy';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { useSkillListActions } from '@/hooks/api/useSkillListActions';
import { createSkillsColumns } from './columns';
import { fetchSkills, skillsQueryKey, type SkillExtraFilters } from './api';
import { SKILLS_TABLE_CONFIG } from './constants';
import { SkillListStateFilterControl } from './SkillListStateFilterControl';
import { SkillRowExpansion } from './SkillRowExpansion';
import { SkillProjectsModal, SkillUsersModal } from './SkillListModals';

interface DataTableSkillsContentProps {
  organizationId: string;
}

/**
 * The rule identity a row writes. `name` is the folded key the matcher
 * stores; `displayName` is only for toasts.
 */
function identityOf(row: SkillDataTableItem): {
  name: string;
  source: string;
  project: string | null;
  displayName: string;
} {
  return {
    name: row.name,
    source: row.source,
    project: row.project,
    displayName: row.displayName,
  };
}

/**
 * Inventory → Skills (KIR-4699).
 *
 * Organization scope only: there is no account-scoped skills route, so the
 * tab is not offered on the personal dashboard.
 */
export function DataTableSkillsContent({
  organizationId,
}: DataTableSkillsContentProps): ReactElement {
  const [listState, setListState] = useState<SkillListStateFilter>('all');
  const [markTarget, setMarkTarget] = useState<SkillDataTableItem | null>(null);
  const [markOpen, setMarkOpen] = useState(false);
  // Row and openness are separate on purpose: the dialog stays mounted while
  // it animates out, so the row has to outlive the close or the title blanks
  // to "(0)" and the empty state flashes on the way out.
  const [projectsTarget, setProjectsTarget] =
    useState<SkillDataTableItem | null>(null);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [usersTarget, setUsersTarget] = useState<SkillDataTableItem | null>(
    null
  );
  const [usersOpen, setUsersOpen] = useState(false);

  const handleRequestMark = useCallback((row: SkillDataTableItem) => {
    setMarkTarget(row);
    setMarkOpen(true);
  }, []);

  const handleProjectsClick = useCallback((row: SkillDataTableItem) => {
    setProjectsTarget(row);
    setProjectsOpen(true);
  }, []);

  const handleUsersClick = useCallback((row: SkillDataTableItem) => {
    setUsersTarget(row);
    setUsersOpen(true);
  }, []);

  // Reading the tab is manager/owner/support, but writing a rule is
  // manager/owner — so a support user must see the toggle disabled rather
  // than click it into a 403.
  const writeGate = useOrgWriteGate(organizationId);
  const canEdit =
    !writeGate.hidden && !writeGate.disabled && !writeGate.isLoading;

  const actions = useSkillListActions(organizationId as OrganizationId);
  const { markForDelete, allow, isPending } = actions;

  const extra = useMemo<SkillExtraFilters>(() => ({ listState }), [listState]);

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchSkills(organizationId, extra, params, signal),
    [organizationId, extra]
  );

  // The pill has to reach the key as well as the request, or switching it
  // would serve the previous pill's rows straight out of cache.
  const getQueryKeys = useCallback(
    (params: DataFetchParams) => skillsQueryKey(organizationId, extra, params),
    [organizationId, extra]
  );

  const handleAllow = useCallback(
    (row: SkillDataTableItem) => {
      void allow(identityOf(row));
    },
    [allow]
  );

  const handleConfirmMark = useCallback(() => {
    if (!markTarget) return;
    void markForDelete(identityOf(markTarget));
    // Close, but KEEP the row: nulling it here dropped the title back to
    // "Mark this skill for delete?" for the length of the exit animation
    // (KIR-4850 review).
    setMarkOpen(false);
  }, [markTarget, markForDelete]);

  const columns = useMemo(
    () =>
      createSkillsColumns({
        onAllow: handleAllow,
        onRequestMark: handleRequestMark,
        isPending: row => isPending(identityOf(row)),
        canEdit,
        // Loading is not "support read-only": the switch stays disabled, but
        // the tooltip would name the wrong reason.
        readOnlyMessage: writeGate.disabled
          ? SUPPORT_READONLY_TOOLTIP
          : undefined,
        onProjectsClick: handleProjectsClick,
        onUsersClick: handleUsersClick,
        organizationId,
        listState,
      }),
    [
      handleAllow,
      handleRequestMark,
      isPending,
      canEdit,
      writeGate.disabled,
      organizationId,
      listState,
      handleProjectsClick,
      handleUsersClick,
    ]
  );

  const features = useMemo(
    () => ({
      enableSorting: true,
      enableRowExpansion: true,
      enableFiltering: true,
    }),
    []
  );

  const rowExpansion = useMemo(
    () => ({
      // The full triple: two rows can legitimately share a name across
      // sources or repositories, and expansion state would collide on name.
      getRowId: (row: SkillDataTableItem) =>
        `${row.source}|${row.project ?? ''}|${row.name}`,
      keepMounted: false,
      render: (row: SkillDataTableItem) => (
        <SkillRowExpansion organizationId={organizationId} row={row} />
      ),
    }),
    [organizationId]
  );

  // `listState` is not a column filter, so it is invisible to the count
  // fingerprint unless named here — without it, switching the pill replays
  // the previous pill's total as a knownTotal.
  const countScope = useMemo(
    () => ({ organizationId, listState }),
    [organizationId, listState]
  );

  const queryConfig = useMemo(
    () => ({ staleTime: 30000, refetchOnWindowFocus: false }),
    []
  );

  const search = useMemo(
    () => ({ placeholder: 'Search skills...', className: 'w-[260px]' }),
    []
  );

  const stateFilter = useMemo(
    () => (
      <SkillListStateFilterControl value={listState} onChange={setListState} />
    ),
    [listState]
  );

  const handleClearFilters = useCallback(() => setListState('all'), []);

  return (
    <>
      <Alert className="mb-3">
        <Info className="h-4 w-4" />
        <AlertDescription>
          Review the agent skills installed across your organization. Marking a
          skill for delete quarantines its files on every endpoint that has it —
          it is an administrative decision, not a security verdict about the
          skill.
        </AlertDescription>
      </Alert>

      <DataTable<SkillDataTableItem>
        id={SKILLS_TABLE_CONFIG.tableId}
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        columns={columns}
        errorBoundaryLabel="Something went wrong in the Skills table"
        search={search}
        features={features}
        rowExpansion={rowExpansion}
        queryFn={queryFn}
        getQueryKeysFromParams={getQueryKeys}
        toolbarEndSlot={stateFilter}
        onClearFilters={handleClearFilters}
        pageResetKey={listState}
        noDataLabel={
          listState === 'marked_for_delete'
            ? 'No skills are marked for delete.'
            : listState === 'allowed'
              ? 'No allowed skills.'
              : 'No skills found.'
        }
        countScope={countScope}
        queryConfig={queryConfig}
      />

      <ConfirmationDialog
        isOpen={markOpen}
        onClose={() => setMarkOpen(false)}
        onConfirm={handleConfirmMark}
        title={`Mark ${markTarget?.displayName ?? 'this skill'} for delete?`}
        // Two things the copy must not imply, both load-bearing:
        //  - that the skill is already gone. A mark is not retroactive; the
        //    agent acts on the file the next time it scans it.
        //  - that Allow restores a file. Allow deletes the rule, which stops
        //    further quarantining. A file already quarantined stays that way
        //    until someone restores it locally on that endpoint (the agent's
        //    `trust_anyway`) (KIR-4850 review).
        description="Kirin will quarantine this skill's files on every endpoint that has it, taking effect the next time each endpoint scans the file rather than immediately. Allowing later stops further quarantining; files already quarantined stay quarantined until restored locally on each endpoint."
        confirmLabel="Mark for delete"
        variant="destructive"
      />

      <SkillProjectsModal
        row={projectsTarget}
        open={projectsOpen}
        onClose={() => setProjectsOpen(false)}
      />
      <SkillUsersModal
        row={usersTarget}
        open={usersOpen}
        onClose={() => setUsersOpen(false)}
      />
    </>
  );
}

export default DataTableSkillsContent;
